import { useState } from 'react';
import { articlesColles } from '../lib/coller-articles';
import { DEVISE, fmt } from '../lib/currency';
import { tr, trf } from '../lib/i18n';
import { C } from '../theme/palette';
import { Btn } from './ui/Btn.jsx';
import { _inp } from './ui/Inputs.jsx';

/**
 * « 📋 Coller une liste d'articles » hors d'une offre : la liste des Notes du
 * téléphone, de Word ou d'Excel devient des articles du catalogue ou du stock,
 * comme elle devient des lignes dans l'offre et la facture.
 *
 * @param {object} props propriétés
 * @param {[string, string][]} props.choix sortes de prix proposées (valeur, libellé) ;
 *   la valeur « ttc » ramène le prix collé hors taxe
 * @param {number} [props.tva] taux de TVA en %, pour les prix TVA comprise
 * @param {string} props.libelleAjout texte du bouton qui ajoute les articles lus
 * @param {(articles: object[], choix: string) => void} props.onAjoute reçoit les
 *   articles lus (`d`, `sd`, `q`, `u`, `p`) et la sorte de prix choisie
 * @returns {import('react').ReactElement} le bouton, puis la case
 */
export function CollerListe({ choix, tva = 8.1, libelleAjout, onAjoute }) {
  const [ouvert, setOuvert] = useState(false);
  const [texte, setTexte] = useState('');
  const [prix, setPrix] = useState(choix[0][0]);
  const [lues, setLues] = useState(null);

  const lire = () => {
    const l = articlesColles(texte, { prix: prix === 'ttc' ? 'ttc' : 'ht', tva });
    if (!l.length) {
      window.alert(
        tr('Aucun article reconnu. Collez une ligne par article, le prix en fin de ligne.'),
      );
      return;
    }
    setLues(l);
  };

  const ferme = () => {
    setTexte('');
    setLues(null);
    setOuvert(false);
  };

  if (!ouvert) {
    return (
      <div style={{ marginBottom: 12 }}>
        <Btn
          label="📋 Coller une liste d'articles (Notes, Word, Excel)"
          color={C.teal}
          ghost
          full
          onClick={() => setOuvert(true)}
        />
      </div>
    );
  }
  return (
    <div
      style={{
        background: '#ecfeff',
        border: '1px solid #a5f3fc',
        borderRadius: 11,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 800, color: '#155e75', marginBottom: 3 }}>
        📋 {tr('Coller une liste d’articles')}
      </div>
      <div style={{ fontSize: 9.5, color: '#0e7490', marginBottom: 8, lineHeight: 1.45 }}>
        {tr('Une ligne par article, le prix à la fin : « Silicone blanc 310 ml   2 x 8.90 ».')}{' '}
        {tr(
          'Ou un bloc par article : le nom, puis la description, le prix à la fin (« 50 CHF m2 »), une ligne vide entre deux articles.',
        )}
      </div>
      {!lues ? (
        <>
          <textarea
            aria-label={tr('Liste d’articles collée')}
            rows={14}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder={
              'Plaque de plâtre 12.5 mm   14.20\nProfilé CW 50   60 pce à 6.85\n\nDémolition\nDépose de l’ancien carrelage, y compris évacuation. 50 CHF m2'
            }
            style={{ ..._inp, fontFamily: 'inherit', resize: 'vertical', minHeight: 260 }}
          />
          {choix.length > 1 && (
            <div
              style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '8px 0', fontSize: 11 }}
            >
              {choix.map(([valeur, libelle]) => (
                <label
                  key={valeur}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
                >
                  <input
                    type="radio"
                    name="prix-liste"
                    checked={prix === valeur}
                    onChange={() => setPrix(valeur)}
                  />
                  {tr(libelle)}
                </label>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <Btn label={tr('Lire la liste')} color={C.teal} full onClick={lire} />
            </div>
            <div style={{ flex: 1 }}>
              <Btn label={tr('Annuler')} color={C.muted} ghost full onClick={ferme} />
            </div>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: '#155e75', marginBottom: 6 }}>
            {trf('{0} article(s) lu(s) — vérifie avant d’ajouter', lues.length)}
          </div>
          <div style={{ background: C.white, borderRadius: 8, marginBottom: 9 }}>
            {lues.map((l, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '7px 9px',
                  borderBottom: i < lues.length - 1 ? '1px solid ' + C.border : 'none',
                  fontSize: 11,
                }}
              >
                <span style={{ flex: 1 }}>
                  <b>{l.d}</b>
                  {l.sd && (
                    <span style={{ display: 'block', color: C.muted, fontSize: 10, marginTop: 2 }}>
                      {l.sd}
                    </span>
                  )}
                </span>
                <span style={{ color: C.muted, whiteSpace: 'nowrap' }}>
                  {l.q} {l.u}
                </span>
                <span
                  style={{
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    color: l.sansPrix ? C.red : C.ink,
                  }}
                >
                  {l.sansPrix ? tr('prix ?') : DEVISE + ' ' + fmt(l.p)}
                </span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Btn
                label={libelleAjout}
                color={C.teal}
                full
                onClick={() => {
                  onAjoute(lues, prix);
                  ferme();
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Btn
                label={tr('Modifier la liste')}
                color={C.muted}
                ghost
                full
                onClick={() => setLues(null)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
